import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { formatDateTime } from '@util/date-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { AssignedBijlage } from '../types/AssignedBijlage'
import type { AssignedBijlageLog } from '../types/AssignedBijlageLog'
import type { Bijlage } from '../types/Bijlage'

import { AssignedBijlageLogStatus } from './AssignedBijlageLogStatus'

type BijlageOrLog = Bijlage | AssignedBijlageLog

const sortBijlagenWithLogs = (a: BijlageOrLog, b: BijlageOrLog) => {
  const extractDate = (x: BijlageOrLog) =>
    'created' in x ? x.created.date : x.date

  return extractDate(a) < extractDate(b) ? -1 : 1
}

interface Props {
  assignedBijlage: AssignedBijlage
  bijlagen: Bijlage[]
  onCancel: (bijlage: Bijlage | AssignedBijlage) => void
  onOpen: (bijlage: Bijlage) => void
}

export const BijlagenWithLogRows = ({
  assignedBijlage,
  bijlagen,
  onCancel,
  onOpen,
}: Props) => {
  const { combi, organisatie } = useAuthenticatedUser()
  const { assignedTo, log, requiresReview } = assignedBijlage
  const isOpgeleverd = assignedBijlage.status === 'done'
  const assignedToOrganisatie = assignedTo === organisatie
  const isInReview = assignedBijlage.status === 'review'

  const bijlagenWithLogs: BijlageOrLog[] = [...bijlagen]

  if (requiresReview) {
    const logs = log.filter(
      (log) =>
        log.status === 'done' ||
        (log.status === 'uploaded' && log.reden) ||
        (log.status === 'assigned' && log.continued),
    )
    bijlagenWithLogs.push(...logs)
    bijlagenWithLogs.sort(sortBijlagenWithLogs)
  }

  return (
    <>
      {bijlagenWithLogs.map((data) => {
        const isBijlage = 'log' in data

        if (isBijlage) {
          const {
            _id,
            naam,
            created: { organisatie, date, email },
            status,
            omschrijving,
          } = data
          const isCancelled = status === 'cancelled'

          return (
            <TableRow
              key={_id}
              sx={{
                backgroundColor: isCancelled ? 'lightgray !important' : '',
              }}
            >
              <TableCell>
                {`${naam}${isCancelled ? ' (geannuleerd)' : ''}`}
                <br />
                <Typography variant="caption">{omschrijving}</Typography>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>{formatDateTime(date)}</TableCell>
              <TableCell>
                {organisatie}

                {email ? (
                  <Typography variant="caption">
                    <br />
                    {email}
                  </Typography>
                ) : (
                  ''
                )}
              </TableCell>
              <TableCell></TableCell>
              <TableCell sx={{ alignSelf: 'right' }}>
                <StyledIconButton
                  title="Bijlage openen"
                  onClick={() => onOpen(data)}
                  icon={<CloudDownloadIcon />}
                />
                {(combi || assignedToOrganisatie) &&
                  !isCancelled &&
                  !isOpgeleverd &&
                  !isInReview && (
                    <StyledIconButton
                      title="Bijlage annuleren"
                      icon={<DeleteIcon />}
                      onClick={() => onCancel(data)}
                    />
                  )}
              </TableCell>
            </TableRow>
          )
        }

        const {
          date,
          organisatie,
          reden,
          status,
          email,
          continued,
          assignedTo,
        } = data
        const rejected = status === 'uploaded' && reden
        const approvedAndContinued = status === 'assigned' && continued
        const approvedAndComplete = status === 'done'

        let description: string = ''

        if (rejected) description = `afgekeurd - ${reden}`
        if (approvedAndContinued)
          description = `goedgekeurd - ${assignedTo} werkt verder`
        if (approvedAndComplete) description = 'goedgekeurd - Afgerond'

        return (
          <TableRow
            key={`${date}-${organisatie}`}
            sx={{
              backgroundColor:
                rejected && !approvedAndContinued
                  ? 'rgba(255, 0, 0, 0.1)'
                  : 'rgba(0, 255, 0, 0.1)',
            }}
          >
            <TableCell>
              {`${assignedBijlage.documentsoort}: ${description}`}
            </TableCell>
            <TableCell></TableCell>
            <TableCell>
              <AssignedBijlageLogStatus
                logEntry={data}
                assignedBijlage={assignedBijlage}
              />
            </TableCell>
            <TableCell>{formatDateTime(date)}</TableCell>
            <TableCell>
              {organisatie}

              {email ? (
                <Typography variant="caption">
                  <br />
                  {email}
                </Typography>
              ) : (
                ''
              )}
            </TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        )
      })}
    </>
  )
}
